import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';

import { TranslatePipe } from '@core/i18n-legacy';
import { AppNavigatorService } from '@core/navigation-legacy';
import { OfferSelectOptionHelper } from '@core/offers-ui';
import type { Placement } from '@core/placements-types';
import { EvaluationsAnalytics, WorkflowTemplatesAnalytics } from '@core/temp-legacy';
import {
  ButtonComponent,
  ConnectedOverlayDirective,
  IconComponent,
  OverlayOriginDirective,
  RtTooltip,
} from '@core/user-interface-legacy';
import { WorkflowTemplatesFeatures } from '@core/workflow-templates-types';
import type { AddEvaluationSubmit } from '@recruitee/evaluations-types';

import { AddEvaluationDropdownComponent } from '../../dropdowns/add-evaluation-dropdown/add-evaluation-dropdown.component';

@Component({
  selector: 'rt-add-evaluation-container',
  templateUrl: './add-evaluation-container.component.html',
  styleUrls: ['./add-evaluation-container.component.less'],
  imports: [
    ButtonComponent,
    IconComponent,
    TranslatePipe,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    AddEvaluationDropdownComponent,
    RtTooltip,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [WorkflowTemplatesAnalytics, EvaluationsAnalytics],
})
export class AddEvaluationContainerComponent {
  private readonly appNavigator = inject(AppNavigatorService);
  private readonly analyticsWorkflowTemplates = inject(WorkflowTemplatesAnalytics);
  private readonly evaluationsAnalytics = inject(EvaluationsAnalytics);

  protected readonly candidateId = input<number>();
  protected readonly availablePlacements = input<Placement[]>([]);
  protected readonly iconOnly = input(false);

  protected readonly offers = computed(() =>
    OfferSelectOptionHelper.placementsToOfferSelectOptions(this.availablePlacements()),
  );

  protected readonly positions: ConnectionPositionPair[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  ];

  protected onAddEvaluation(data: AddEvaluationSubmit): void {
    const candidateId = this.candidateId();

    if (!candidateId) return;

    this.analyticsWorkflowTemplates.trackTemplateInsertion(
      WorkflowTemplatesFeatures.EvaluationForms,
    );
    this.evaluationsAnalytics.trackRequestEvaluation();

    this.appNavigator.openCandidateDialog(candidateId, {
      openInterview: true,
      interviewOfferId: data.offerId,
      interviewTalentPoolId: data.talentPoolId,
      interviewTemplateId: data.templateId,
    });
  }

  protected onAddQuickEvaluation(): void {
    const candidateId = this.candidateId();

    if (!candidateId) return;

    this.appNavigator.openCandidateDialog(candidateId, {
      openInterview: true,
      quickEvaluation: true,
    });
  }
}
